import moment, { type MomentInput, type unitOfTime } from 'moment';
import momentStrftime from 'moment-strftime';
import '@aha-app/moment-fquarter';
import 'javascripts/moment-business';
import { MONTH_NUMBERS } from 'javascripts/constants/dates';

const DATE_FORMAT = 'YYYY-MM-DD';

export const dateAdd = (
  date: MomentInput,
  count: number,
  { period = 'days', business = false } = {}
) => {
  const addFn = business ? 'businessAdd' : 'add';
  if (business && !['days', 'day'].includes(period)) {
    console.warn(
      `Invoked 'dateAdd' with period '${period}' and 'business', but 'business' adding only supports days.`
    );
  }
  return moment.utc(date)[addFn](count, period).format(DATE_FORMAT);
};

export const dateFormat = (date: MomentInput, format = DATE_FORMAT) =>
  moment.utc(date).format(format);

export function formatDateToLocale(
  date: string | moment.Moment | Date,
  fallbackFormat = DATE_FORMAT
) {
  // Ensure the date is a moment object.
  date = moment.isMoment(date) ? date : moment(date);

  if (!date.isValid()) return date;

  const formattedDate = new Intl.DateTimeFormat(navigator?.language)?.format(
    // Strip all time/timezone info, so we don't have to worry about UTC offset.
    new Date(date.year(), date.month(), date.date())
  );

  return formattedDate ?? dateFormat(date, fallbackFormat);
}

export const dateDiff = (
  startDateInput: MomentInput,
  endDateInput: MomentInput,
  { business = false } = {}
) => {
  const startDate = moment.utc(startDateInput);
  const endDate = moment.utc(endDateInput);
  const diffFn = business ? 'businessDiff' : 'diff';
  return endDate[diffFn](startDate, 'days');
};

export const dateIsSame = (dateA: MomentInput, dateB: MomentInput) =>
  moment.utc(dateA).isSame(moment.utc(dateB), 'day');

export const dateSubtract = (
  date: MomentInput,
  count: number,
  opts: Parameters<typeof dateAdd>[2] = {}
) => dateAdd(date, -count, opts);

export const dateMax = (...dates: MomentInput[]) =>
  moment
    .max(dates.filter(date => Boolean(date)).map(date => moment.utc(date)))
    .format(DATE_FORMAT);

export const dateMin = (...dates: MomentInput[]) =>
  moment
    .min(dates.filter(date => Boolean(date)).map(date => moment.utc(date)))
    .format(DATE_FORMAT);

export const dateClamp = (
  date: MomentInput,
  { min, max }: { min: MomentInput; max: MomentInput }
) => dateMax(dateMin(date, max), min);

export const dateStartOf = (date: MomentInput, span: unitOfTime.StartOf) =>
  moment.utc(date).startOf(span).format(DATE_FORMAT);

export const dateFormattedWithUserLocale = (date: MomentInput) => {
  const dateFormat = window.currentUser?.dateFormat || '%m/%d/%Y';
  return momentStrftime(date).strftime(dateFormat);
};

export const dateMonthIndex = (date: MomentInput) => moment.utc(date).month();

export const getYear = (date: MomentInput) => moment(date, DATE_FORMAT).year();

export const getMonthIndex = (date: MomentInput) =>
  moment(date, DATE_FORMAT).month();

/**
 * Returns a date at the beginning of the given year and month.
 * @example
 * // returns '2021-11-01'
 * monthYearToDate(2021, 10);
 * @returns Returns the date at the beginning of the month as a string.
 */
export const monthYearToDate = (year: number, month: number) => {
  return moment.utc([year, month]).format(DATE_FORMAT);
};

/**
 * Returns the date for the first day of the fiscal year as a string.
 * @params date - The date for which we want to know the start of the fiscal year.
 * @params [fiscalMonth=1] - The first month of the fiscal year (1-12).
 * @returns A date as a string.
 */
export const startOfFiscalYear = (
  date: MomentInput,
  { fiscalMonth = MONTH_NUMBERS.JANUARY } = {}
) => {
  let year = getYear(date);

  // months in JavaScript dates go from 0-11, but the fiscalMonth goes from 1-12 so we subtract 1 to the fiscal month
  const fiscalMonthIndex = fiscalMonth - 1;
  if (getMonthIndex(date) < fiscalMonthIndex) {
    year = year - 1;
  }

  return moment.utc([year, fiscalMonth - 1]).format(DATE_FORMAT);
};

/**
 * Returns the fiscal information for the given date and fiscal month.
 * @returns {{ quarter: number, year: number, nextYear: number | null }}
 */
export const dateFiscal = (
  date: MomentInput,
  { fiscalMonth = MONTH_NUMBERS.JANUARY } = {}
) => {
  return moment.utc(date).fquarter(fiscalMonth);
};

export const dateNow = () => moment.utc().format(DATE_FORMAT);

export const dateIsBefore = (a: MomentInput, b: MomentInput) =>
  moment.utc(a).isBefore(moment.utc(b));
export const dateIsSameOrBefore = (a: MomentInput, b: MomentInput) =>
  moment.utc(a).isSameOrBefore(moment.utc(b));

export const dateIsAfter = (a: MomentInput, b: MomentInput) =>
  moment.utc(a).isAfter(moment.utc(b));
export const dateIsSameOrAfter = (a: MomentInput, b: MomentInput) =>
  moment.utc(a).isSameOrAfter(moment.utc(b));

export const dateIsWeekDay = (date: MomentInput) => {
  return moment.utc(date).isoWeekday() < 6;
};

export const dateIsWeekendDay = (date: MomentInput) => !dateIsWeekDay(date);

export function dateCurrentOrNextWeekDay<T extends MomentInput>(date: T) {
  if (dateIsWeekDay(date)) {
    return date;
  } else {
    // Saturday is 6, Sunday is 7
    const utcDate = moment.utc(date);
    return utcDate.add(8 - utcDate.isoWeekday(), 'days').format(DATE_FORMAT);
  }
}

export function dateCurrentOrPreviousWeekDay<T extends MomentInput>(date: T) {
  if (dateIsWeekDay(date)) {
    return date;
  } else {
    // Saturday is 6, Sunday is 7
    const utcDate = moment.utc(date);
    return utcDate
      .subtract(utcDate.isoWeekday() - 5, 'days')
      .format(DATE_FORMAT);
  }
}

export const dateStartOfWeekUsingPreference = (date: MomentInput) =>
  moment
    .utc(date)
    .startOf(window.currentUser?.startDay === 0 ? 'week' : 'isoWeek');
